// TaskManagerPage.js
import React, { useState, useEffect } from 'react';
import TaskManager from './TaskManager';
import ChecklistManager from './ChecklistManager';
import MonthlyDashboard from './MonthlyDashboard';
import ChecklistsDashboard from './ChecklistsDashboard';
import useAuth from '../hooks/useAuth';
import { canDo } from '../utils/canDo';
import api from './api';
import './TaskManagerPage.css';

const SUPER_ROLES = ['owner', 'solution', 'supervisor'];

const TaskManagerPage = () => {
  const { auth } = useAuth();
  const [activeTab, setActiveTab] = useState('tasks');

  // 'all' or a specific username; default to logged-in user
  const [userFilter, setUserFilter] = useState(auth?.username || 'all');

  const [users, setUsers] = useState([]);

  const canViewClDashboard = (auth?.roles || []).some(r => SUPER_ROLES.includes(r));

  useEffect(() => {
    api.get(`${process.env.REACT_APP_URL}/users/therapist`)
      .then(res => setUsers(res.data || []))
      .catch(() => {});
  }, []);

  const isSuperadmin = canDo(auth, 'checklist.edit');

  return (
    <div className="task-manager-page">

      {/* ── Top filter bar ─────────────────────────────────── */}
      {activeTab !== 'dashboard' && activeTab !== 'cl-dashboard' && (
        <div className="tmp-filter-bar">
          <div className="tmp-filter-label">Viewing tasks for:</div>
          <div className="tmp-filter-toggle">
            <button
              className={`tmp-toggle-btn ${userFilter !== 'all' ? 'active' : ''}`}
              onClick={() => setUserFilter(auth?.username)}
            >
              <span className="tmp-toggle-icon">👤</span>
              {auth?.username || 'Me'}
            </button>
            {isSuperadmin && (
              <button
                className={`tmp-toggle-btn ${userFilter === 'all' ? 'active' : ''}`}
                onClick={() => setUserFilter('all')}
              >
                <span className="tmp-toggle-icon">👥</span>
                All
              </button>
            )}
          </div>

          {/* User dropdown for superadmin — pick any individual */}
          {isSuperadmin && users.length > 0 && (
            <select
              className="tmp-user-select"
              value={userFilter}
              onChange={e => setUserFilter(e.target.value)}
            >
              <option value="all">All Users</option>
              {users.map(u => (
                <option key={u.username || u._id} value={u.username}>
                  {u.name || u.username}
                  {u.username === auth?.username ? ' (me)' : ''}
                </option>
              ))}
            </select>
          )}
        </div>
      )}

      {/* ── Tabs ───────────────────────────────────────────── */}
      <div className="tabs">
        <button
          className={activeTab === 'tasks' ? 'active' : ''}
          onClick={() => setActiveTab('tasks')}
        >
          Tasks
        </button>
        <button
          className={activeTab === 'checklist' ? 'active' : ''}
          onClick={() => setActiveTab('checklist')}
        >
          Checklists
        </button>
        {isSuperadmin && (
          <button
            className={activeTab === 'dashboard' ? 'active' : ''}
            onClick={() => setActiveTab('dashboard')}
          >
            Dashboard
          </button>
        )}
        {canViewClDashboard && (
          <button
            className={activeTab === 'cl-dashboard' ? 'active' : ''}
            onClick={() => setActiveTab('cl-dashboard')}
          >
            CL Report
          </button>
        )}
      </div>

      {/* ── Tab content ────────────────────────────────────── */}
      <div className="tab-content">
        {activeTab === 'tasks' && <TaskManager userFilter={userFilter} />}
        {activeTab === 'checklist' && <ChecklistManager userFilter={userFilter} />}
        {activeTab === 'dashboard' && <MonthlyDashboard />}
        {activeTab === 'cl-dashboard' && <ChecklistsDashboard />}
      </div>
    </div>
  );
};

export default TaskManagerPage;
